import { useEffect, useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { inputClass } from './reviewStyles'
import type { IntakeConfidence } from './aiIntake'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { AreaReading, AreaUnit, UnitChoice } from './area'

/**
 * The pieces the review is built from. It is a form, not a summary: every
 * field is an input the agent can type in straight away, filled with what the
 * AI found and empty where it found nothing. A red ring means the listing
 * form will not submit without it.
 *
 * Typing does not save on every keystroke; a field keeps what was typed and
 * hands it over when it loses focus or on Enter, which is also when the
 * conversation's saved copy is updated.
 */

export function SectionTitle({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
    return (
        <div className='mb-2 flex items-center justify-between'>
            <h4 className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>{children}</h4>
            {aside}
        </div>
    )
}

export function FieldRow({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
    return (
        <div className='grid grid-cols-1 items-center gap-x-4 gap-y-1 py-1.5 text-sm sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)]'>
            <dt className='text-muted-foreground'>
                {label}
                {required && <span className='ml-0.5 text-red-600'>*</span>}
            </dt>
            <dd className='min-w-0'>{children}</dd>
        </div>
    )
}

/**
 * A text or number field. `value` is what the draft holds; what is typed is
 * kept here until the field is left, then handed over once.
 */
export function TextField({
    value,
    onCommit,
    placeholder,
    required,
    multiline,
    numeric,
    whole,
    money,
    suffix,
    disabled,
    problem,
}: {
    value: string
    onCommit: (next: string) => void
    placeholder?: string
    required?: boolean
    multiline?: boolean
    /** Takes digits only (and one decimal point unless `whole`); letters cannot be typed in. */
    numeric?: boolean
    whole?: boolean
    /** Shows as 15,047,300 while it is typed; the draft keeps the plain number. */
    money?: boolean
    suffix?: string
    disabled?: boolean
    /** What is wrong with the value, shown under the field. */
    problem?: string | null
}) {
    const grouped = (raw: string) => {
        const [whole, ...rest] = raw.replace(/,/g, '').split('.')
        const digits = whole.replace(/\D/g, '')
        return [digits ? Number(digits).toLocaleString('en-MY') : '', ...rest].join('.')
    }
    const numbersOnly = (raw: string) => {
        const [first, ...rest] = raw.replace(/[^\d.]/g, '').split('.')
        return whole || !rest.length ? first : `${first}.${rest.join('')}`
    }
    const shown = (raw: string) => (money ? grouped(numbersOnly(raw)) : numeric ? numbersOnly(raw) : raw)

    const [text, setText] = useState(shown(value))
    // A new draft, or an answer from the AI, replaces what is shown.
    useEffect(() => setText(shown(value)), [value, money, numeric, whole])

    const plain = (raw: string) => (money ? raw.replace(/,/g, '') : raw)
    const commit = () => plain(text) !== value && onCommit(plain(text))
    const common = {
        value: text,
        placeholder: placeholder ?? (required ? 'Required' : 'Not provided'),
        disabled,
        onChange: (e: { target: { value: string } }) => setText(shown(e.target.value)),
        onBlur: commit,
        onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                commit()
                ;(e.target as HTMLElement).blur()
            }
            if (e.key === 'Escape') {
                e.stopPropagation()
                setText(shown(value))
            }
        },
        className: inputClass(text.trim() === '', required, Boolean(problem)),
    }

    const note = problem && <span className='mt-0.5 block text-[11px] text-red-600'>{problem}</span>
    if (multiline) return (
        <>
            <textarea rows={7} {...common} />
            {note}
        </>
    )
    return (
        <>
            <div className='relative'>
                <input inputMode={numeric ? (whole ? 'numeric' : 'decimal') : undefined} {...common} className={cn(common.className, suffix && 'pr-12')} />
                {suffix && <span className='pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground'>{suffix}</span>}
            </div>
            {note}
        </>
    )
}

/**
 * An area: the figure, and the unit it is shown in. Square feet unless the
 * agent picks another of the units the listing form offers; picking one
 * converts the figure, since the land has not changed. The figure is grouped
 * the way a price is (1,190,059), and the square feet the listing stores are
 * shown underneath whenever the unit is anything else.
 */
export function AreaField({
    reading,
    units,
    required,
    disabled,
    onChange,
    onUnit,
}: {
    reading: AreaReading
    units: UnitChoice[]
    required?: boolean
    disabled?: boolean
    onChange: (value: number | null, unit: AreaUnit) => void
    onUnit: (unit: AreaUnit) => void
}) {
    const typed = (raw: string) => {
        const n = Number(raw.replace(/,/g, ''))
        return raw.trim() && Number.isFinite(n) && n > 0 ? n : null
    }
    return (
        <>
            <div className='flex items-start gap-2'>
                <div className='min-w-0 flex-1'>
                    <TextField value={reading.value === null ? '' : String(reading.value)} required={required} numeric money whole={reading.unit === 'sqft'} disabled={disabled} onCommit={(raw) => onChange(typed(raw), reading.unit)} />
                </div>
                <Select disabled={disabled || units.length < 2} value={reading.unit} onValueChange={(unit) => onUnit(unit as AreaUnit)}>
                    <SelectTrigger size='sm' aria-label='Unit' className={cn(inputClass(false), 'h-auto w-32 shrink-0 disabled:opacity-100')}>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {units.map((u) => (
                            <SelectItem key={u.value} value={u.value}>
                                {u.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            {reading.unit !== 'sqft' && reading.sqft !== null && (
                <span className='mt-0.5 block text-[11px] text-muted-foreground'>= {reading.sqft.toLocaleString('en-MY')} sq ft</span>
            )}
        </>
    )
}

/** Yes or no, the way the form asks it: neither chosen until one is. */
export function BoolField({ value, onChange, disabled }: { value: boolean | null; onChange: (next: boolean) => void; disabled?: boolean }) {
    return (
        <div className='flex gap-1.5'>
            {[true, false].map((option) => (
                <button
                    key={String(option)}
                    type='button'
                    disabled={disabled}
                    onClick={() => onChange(option)}
                    className={cn(
                        'h-8 cursor-pointer rounded-md border px-3 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                        value === option ? 'border-black bg-black text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-400',
                    )}>
                    {option ? 'Yes' : 'No'}
                </button>
            ))}
        </div>
    )
}

const CONFIDENCE: Record<IntakeConfidence, { label: string; tone: string; dot: string }> = {
    high: { label: 'High confidence', tone: 'bg-green-50 text-green-700 ring-green-600/20', dot: 'bg-green-600' },
    medium: { label: 'Medium confidence', tone: 'bg-amber-50 text-amber-700 ring-amber-600/20', dot: 'bg-amber-500' },
    low: { label: 'Low confidence', tone: 'bg-red-50 text-red-700 ring-red-600/20', dot: 'bg-red-600' },
}

/** How sure the AI is, in words and colour, with its reasoning on hover. */
export function ConfidenceBadge({ level, title }: { level: IntakeConfidence; title?: string }) {
    const c = CONFIDENCE[level] ?? CONFIDENCE.low
    return (
        <span title={title} className={cn('inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset', c.tone)}>
            <span className={cn('h-1.5 w-1.5 rounded-full', c.dot)} />
            {c.label}
        </span>
    )
}
